/**
 * Personalisation field overrides
 * - Reduces maxlength to 12 characters
 * - Validates allowed characters, selected emojis, and restricted words
 * - Disables checkout buttons until the text is acceptable
 */
(function () {
  'use strict';

  const MAX_LENGTH = 12;
  const MAX_PRODUCT_QUANTITY = 2;
  const VALIDATION_MESSAGE =
    "Please recheck and edit the personalisation text. Some words, symbols, or emojis cannot be printed on a children's toy.";
  const MULTI_QUANTITY_MESSAGE =
    'For personalizing more than 1 toy, please keep quantity as 1, type your text and add each toy to the cart separately.';
  const MAX_QUANTITY_MESSAGE = 'For higher quantity, kindly get in touch with us.';

  // All emojis are allowed by default.
  // Add inappropriate emojis here to block them.
  const BLOCKED_EMOJIS = new Set([
    '🍆',
    '🍑',
    '💦',
    '🖕',
    '💩',
    '🔞',
    '🍺',
    '🍻',
    '🍷',
    '🥂',
    '🔪',
    '🗡️',
    '⚔️',
    '💣',
    '🔫',
    '🚬',
    '💊',
    '💉'
  ]);

  // Keep this intentionally easy to edit. Add lowercase words only.
  const RESTRICTED_WORDS = [
    'abuse',
    'adult',
    'bastard',
    'behenchod',
    'bhenchod',
    'bitch',
    'boob',
    'boobs',
    '80085',
    'chutia',
    'chutiya',
    'chootia',
    'chootiya',
    'cock',
    'dick',
    'fuck',
    'hate',
    'hitler',
    'kill',
    'madarchod',
    'mofo',
    'motherfucker',
    'nazi',
    'nude',
    'porn',
    'pussy',
    'sex',
    'shit',
    'sisterfucker',
    'slut',
    'suck',
    'terror',
    'xxx'
  ];

  const DISALLOWED_EMOJI_REGEX =
    /(\p{Extended_Pictographic}(?:\uFE0F|\u200D\p{Extended_Pictographic})*|\p{Emoji_Presentation})/gu;
  const ALLOWED_TEXT_REGEX = /^[\p{L}\p{N}\s.'’&()+,\-!?/]*$/u;
  const PROPERTY_SELECTOR = 'product-custom-property-component input[type="text"], product-custom-property-component textarea';
  const BUTTON_SELECTOR = 'button[name="add"], button[type="submit"], .shopify-payment-button__button';

  function initPersonalisationOverrides() {
  document.querySelectorAll('product-custom-property-component').forEach(function (component) {
    initPersonalisationComponent(component);
    // Makes only the words "Add to cart" bold inside the personalisation subtitle.
    emphasisePersonalisationDescriptionCta(component);
  });
}

function emphasisePersonalisationDescriptionCta(component) {
  // Finds the subtitle under "Personalise your Math Champion".
  const description = component.querySelector('.__description');
  if (!description) return;

  // Prevents this from running again on the same text if Shopify re-renders the section.
  if (description.dataset.tsfCtaBoldReady === 'true') return;

  const phrase = 'Add to cart';
  const text = description.textContent;
  const index = text.indexOf(phrase);

  // If the exact words are not found, do nothing.
  if (index === -1) return;

  // Rebuilds the subtitle as:
  // normal text + bold "Add to cart" + normal text.
  description.textContent = '';
  description.append(document.createTextNode(text.slice(0, index)));

  const strong = document.createElement('strong');
  strong.textContent = phrase;
  description.append(strong);

  description.append(document.createTextNode(text.slice(index + phrase.length)));
  description.dataset.tsfCtaBoldReady = 'true';
}

  function initPersonalisationComponent(component) {
    const input = component.querySelector('input[type="text"], textarea');
    if (!input) return;
    if (input.dataset.personalisationValidationReady === 'true') return;
    input.dataset.personalisationValidationReady = 'true';

    // --- Override max length ---
    input.setAttribute('maxlength', MAX_LENGTH);

    // --- Update the character count display ---
    const countEl = component.querySelector('[data-template]');
    if (countEl) {
      const template = countEl.getAttribute('data-template');
      // Update the max value in the template
      const newTemplate = template.replace(/\[max\]/g, MAX_LENGTH);
      countEl.setAttribute('data-template', newTemplate);

      // Refresh the displayed count
      const current = input.value.length;
      countEl.textContent = newTemplate.replace('[current]', current);
    }

    const messageEl = createMessageElement(input);
    const form = getProductForm(input);
    const buttons = getCheckoutButtons(form);
    const quantityInput = getQuantityInput(form);

    input.addEventListener('input', function () {
      refreshCharacterCount(input, countEl);
      applyValidation(input, messageEl, buttons, form);
    });

    input.addEventListener('blur', function () {
      applyValidation(input, messageEl, buttons, form);
    });

    if (form) {
      if (quantityInput) {
        quantityInput.addEventListener('input', function () {
          applyValidation(input, messageEl, buttons, form);
        });

        quantityInput.addEventListener('change', function () {
          applyValidation(input, messageEl, buttons, form);
        });
      }

      form.addEventListener('submit', function (event) {
        const isValid = applyValidation(input, messageEl, buttons, form);
        if (!isValid) {
          event.preventDefault();
          event.stopPropagation();
          input.focus();
        }
      }, true);

      buttons.forEach(function (button) {
        button.addEventListener('click', function (event) {
          const isValid = applyValidation(input, messageEl, buttons, form);
          if (!isValid) {
            event.preventDefault();
            event.stopPropagation();
            input.focus();
          }
        }, true);
      });
    }

    applyValidation(input, messageEl, buttons, form);
  }

  function createMessageElement(input) {
    const wrapper = input.closest('.__input-wrapper') || input.parentElement;
    let messageEl = wrapper.nextElementSibling;
    if (!messageEl || !messageEl.classList.contains('personalisation-validation-message')) {
      messageEl = wrapper.parentElement
        ? wrapper.parentElement.querySelector('.personalisation-validation-message')
        : null;
    }
    if (messageEl) return messageEl;

    messageEl = document.createElement('p');
    messageEl.className = 'personalisation-validation-message';
    messageEl.setAttribute('role', 'alert');
    messageEl.setAttribute('aria-hidden', 'true');
    messageEl.textContent = VALIDATION_MESSAGE;
    wrapper.insertAdjacentElement('afterend', messageEl);
    return messageEl;
  }

  function getProductForm(input) {
    const formId = input.getAttribute('form');
    return formId ? document.getElementById(formId) : input.closest('form');
  }

  function getCheckoutButtons(form) {
    if (!form) return [];

    return Array.from(
      form.querySelectorAll(BUTTON_SELECTOR)
    );
  }

  function getQuantityInput(form) {
    if (!form) return null;
    return form.querySelector('input[name="quantity"]');
  }

  function getQuantityValue(form) {
    const quantityInput = getQuantityInput(form);
    if (!quantityInput) return 1;

    const value = parseInt(quantityInput.value, 10);
    return Number.isFinite(value) ? value : 1;
  }

  function applyValidation(input, messageEl, buttons, form) {
    const result = validatePersonalisationText(input.value, getQuantityValue(form));
    const isValid = result.valid;

    input.setAttribute('aria-invalid', String(!isValid));
    messageEl.classList.toggle('is-visible', !isValid);
    messageEl.setAttribute('aria-hidden', String(isValid));
    messageEl.textContent = '';
    syncPersonalisationValidationToast(isValid ? '' : result.message || VALIDATION_MESSAGE);

    buttons.forEach(function (button) {
      if (!isValid) {
        if (!button.disabled) {
          button.dataset.personalisationDisabled = 'true';
        }
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      } else if (button.dataset.personalisationDisabled === 'true') {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        delete button.dataset.personalisationDisabled;
      }
    });

    return isValid;
  }

  function validatePersonalisationText(value, quantity) {
    const text = value.trim();

    if (!text) {
      return { valid: true };
    }

    if (hasDisallowedEmoji(text)) {
      return { valid: false, message: VALIDATION_MESSAGE };
    }

    const textWithoutEmojis = removeEmojis(text);
    if (!ALLOWED_TEXT_REGEX.test(textWithoutEmojis)) {
      return { valid: false, message: VALIDATION_MESSAGE };
    }

    if (containsRestrictedWord(textWithoutEmojis)) {
      return { valid: false, message: VALIDATION_MESSAGE };
    }

    if (quantity > 1) {
      return { valid: false, message: MULTI_QUANTITY_MESSAGE };
    }

    return { valid: true };
  }

  /*
   * Personalisation validation toast
   * Shows the same validation text at the bottom while the field is invalid.
   */
  function syncPersonalisationValidationToast(message) {
    const existingToast = document.querySelector('.personalisation-validation-toast');

    if (!message) {
      existingToast?.classList.remove('is-visible');
      return;
    }

    const toast = existingToast || createPersonalisationValidationToast();
    toast.textContent = message;
    toast.classList.add('is-visible');
  }

  function createPersonalisationValidationToast() {
    const toast = document.createElement('div');
    toast.className = 'personalisation-validation-toast';
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'polite');
    document.body.appendChild(toast);
    return toast;
  }

  function hasDisallowedEmoji(text) {
    const emojiMatches = text.match(DISALLOWED_EMOJI_REGEX) || [];
    return emojiMatches.some(function (emoji) {
      return BLOCKED_EMOJIS.has(emoji);
    });
  }

  function removeEmojis(text) {
    return text.replace(DISALLOWED_EMOJI_REGEX, '');
  }

  function containsRestrictedWord(text) {
    const normalized = text
      .toLowerCase()
      .replace(/[@]/g, 'a')
      .replace(/[!1|]/g, 'i')
      .replace(/[0]/g, 'o')
      .replace(/[$5]/g, 's')
      .replace(/[^a-z0-9]+/g, ' ');

    return RESTRICTED_WORDS.some(function (word) {
      return new RegExp('(^|\\s)' + escapeRegExp(word) + '(\\s|$)', 'i').test(normalized);
    });
  }

  function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function refreshCharacterCount(input, countEl) {
    if (!countEl) return;

    const tmpl = countEl.getAttribute('data-template');
    countEl.textContent = tmpl
      .replace('[current]', input.value.length)
      .replace('[max]', MAX_LENGTH);
  }

  document.addEventListener('submit', function (event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const input = getFormPersonalisationInput(form);
    if (!input) return;

    const messageEl = createMessageElement(input);
    const buttons = getCheckoutButtons(form);
    const isValid = applyValidation(input, messageEl, buttons, form);

    if (!isValid) {
      event.preventDefault();
      event.stopPropagation();
      input.focus();
    }
  }, true);

  document.addEventListener('click', function (event) {
    const button = event.target.closest(BUTTON_SELECTOR);
    if (!button) return;

    const form = button.closest('form') || document.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return;

    const input = getFormPersonalisationInput(form);
    if (!input) return;

    const messageEl = createMessageElement(input);
    const buttons = getCheckoutButtons(form);
    const isValid = applyValidation(input, messageEl, buttons, form);

    if (!isValid) {
      event.preventDefault();
      event.stopPropagation();
      input.focus();
    }
  }, true);

  function getFormPersonalisationInput(form) {
    const formId = form.getAttribute('id');
    const inputs = Array.from(document.querySelectorAll(PROPERTY_SELECTOR));

    return inputs.find(function (input) {
      return input.getAttribute('form') === formId || input.closest('form') === form;
    });
  }

  document.addEventListener('input', function (event) {
    if (!event.target.matches('input[name="quantity"]')) return;
    validateFormFromQuantityInput(event.target);
  });

  document.addEventListener('change', function (event) {
    if (!event.target.matches('input[name="quantity"]')) return;
    validateFormFromQuantityInput(event.target);
  });

  function validateFormFromQuantityInput(quantityInput) {
    const form = quantityInput.closest('form') || document.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return;

    const input = getFormPersonalisationInput(form);
    if (!input) return;

    const messageEl = createMessageElement(input);
    const buttons = getCheckoutButtons(form);
    applyValidation(input, messageEl, buttons, form);
  }

  /*
   * Quantity and personalisation sync
   * Shopify changes the quantity value programmatically when +/- is clicked.
   * This watcher re-runs validation whenever that value changes, without
   * interfering with Shopify's quantity button logic.
   */
  function syncPersonalisationValidationFromQuantity() {
    document.querySelectorAll('form[data-type="add-to-cart-form"] input[name="quantity"]').forEach(function (quantityInput) {
      const currentQuantity = quantityInput.value;
      if (quantityInput.dataset.personalisationLastQuantity === currentQuantity) return;

      quantityInput.dataset.personalisationLastQuantity = currentQuantity;
      validateFormFromQuantityInput(quantityInput);
    });
  }

  setInterval(syncPersonalisationValidationFromQuantity, 150);

  /*
   * Maximum product quantity limit
   * Keeps Add to cart and Buy it now quantities capped at 2.
   */
  function initMaxProductQuantityLimit() {
    document.querySelectorAll('form[data-type="add-to-cart-form"] input[name="quantity"]').forEach(function (quantityInput) {
      enforceMaxProductQuantity(quantityInput, false);
    });
  }

  function enforceMaxProductQuantity(quantityInput, shouldShowToast) {
    const value = parseInt(quantityInput.value, 10);
    if (!Number.isFinite(value)) return true;

    if (value > MAX_PRODUCT_QUANTITY) {
      quantityInput.value = String(MAX_PRODUCT_QUANTITY);
      quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));

      if (shouldShowToast) {
        showMaxQuantityToast();
      }

      return false;
    }

    return true;
  }

  function showMaxQuantityToast() {
    let toast = document.querySelector('.quantity-limit-toast');

    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'quantity-limit-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }

    toast.textContent = MAX_QUANTITY_MESSAGE;
    toast.classList.add('is-visible');

    clearTimeout(showMaxQuantityToast.hideTimer);
    showMaxQuantityToast.hideTimer = setTimeout(function () {
      toast.classList.remove('is-visible');
    }, 4400);
  }

  document.addEventListener('click', function (event) {
    const plusButton = event.target.closest('.quantity-plus');
    if (!plusButton) return;

    const quantityInput = plusButton
      .closest('quantity-selector-component')
      ?.querySelector('input[name="quantity"]');

    if (!quantityInput) return;

    const quantityBeforeClick = parseInt(quantityInput.value, 10);

    setTimeout(function () {
      const quantityAfterClick = parseInt(quantityInput.value, 10);
      const triedToIncreasePastLimit =
        Number.isFinite(quantityBeforeClick) &&
        Number.isFinite(quantityAfterClick) &&
        quantityBeforeClick >= MAX_PRODUCT_QUANTITY &&
        quantityAfterClick >= MAX_PRODUCT_QUANTITY;

      enforceMaxProductQuantity(quantityInput, triedToIncreasePastLimit);
    }, 0);
  }, true);

  document.addEventListener('input', function (event) {
    if (!event.target.matches('input[name="quantity"]')) return;
    enforceMaxProductQuantity(event.target, true);
  });

  document.addEventListener('change', function (event) {
    if (!event.target.matches('input[name="quantity"]')) return;
    enforceMaxProductQuantity(event.target, true);
  });

  document.addEventListener('submit', function (event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const quantityInput = getQuantityInput(form);
    if (!quantityInput) return;

    const isWithinLimit = enforceMaxProductQuantity(quantityInput, true);
    if (!isWithinLimit) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, true);

  const observer = new MutationObserver(function () {
    initPersonalisationOverrides();
    initMaxProductQuantityLimit();
  });

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initPersonalisationOverrides();
      initMaxProductQuantityLimit();
      observer.observe(document.body, { childList: true, subtree: true });
    });
  } else {
    // DOM already loaded, but custom elements may not be rendered yet
    setTimeout(function () {
      initPersonalisationOverrides();
      initMaxProductQuantityLimit();
    }, 500);
    observer.observe(document.body, { childList: true, subtree: true });
  }
  
  /*
 * Price text cleanup.
 * Replaces "Rs." with "₹" on homepage and product page prices.
 * Works on both desktop and mobile because it changes the actual text content.
 */
function replaceRsWithRupeeSymbol() {
  const page = document.querySelector('main[data-template="index"], main[data-template^="product"]');
  if (!page) return;

  page.querySelectorAll('.price, .compare-at-price').forEach(function (priceEl) {
    priceEl.childNodes.forEach(function (node) {
      if (node.nodeType !== Node.TEXT_NODE) return;
      node.textContent = node.textContent.replace(/Rs\.\s*/g, '₹');
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', replaceRsWithRupeeSymbol);
} else {
  replaceRsWithRupeeSymbol();
}
})();